import React from "react";

export function DataTable({ columns, rows, empty = "No records found.", rowKey = "id", onRowClick, testid }) {
  return (
    <div className="panel overflow-hidden" data-testid={testid}>
      <div
        className="overflow-auto"
        style={{ maxHeight: "calc(100vh - 260px)" }}
      >
        <table className="min-w-full text-sm">
          <thead className="bg-stone-100 border-b-2 border-stone-200 sticky top-0 z-10">
            <tr>
              {columns.map((c, i) => (
                <th
                  key={i}
                  className={`text-left font-semibold text-stone-700 text-xs uppercase tracking-wider px-3 py-2.5 whitespace-nowrap bg-stone-100 ${c.align === "right" ? "text-right" : ""}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-12 text-center text-stone-400 text-sm">
                  {empty}
                </td>
              </tr>
            )}
            {rows.map((row, ri) => (
              <tr
                key={row[rowKey] || ri}
                className={`border-b border-stone-100 hover:bg-stone-50 ${onRowClick ? "cursor-pointer" : ""}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                data-testid={`row-${row[rowKey]}`}
              >
                {columns.map((c, ci) => (
                  <td
                    key={ci}
                    className={`px-3 py-2 align-middle whitespace-nowrap ${c.align === "right" ? "text-right" : ""} ${c.mono ? "font-mono text-xs" : ""}`}
                  >
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Toolbar({ children }) {
  return (
    <div className="flex items-center gap-2 flex-wrap mb-4">
      {children}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search...", testid }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 min-w-[220px]"
      data-testid={testid}
    />
  );
}

export function Btn({ children, variant = "primary", as: As = "button", testid, className = "", ...props }) {
  const variants = {
    primary: "bg-emerald-900 hover:bg-emerald-800 text-white",
    secondary: "bg-white text-stone-900 border border-stone-300 hover:bg-stone-50",
    danger: "bg-red-600 hover:bg-red-700 text-white",
    ghost: "text-stone-700 hover:bg-stone-100",
  };
  return (
    <As
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-50 ${variants[variant]} ${className}`}
      data-testid={testid}
      {...props}
    >
      {children}
    </As>
  );
}

export function Field({ label, children, required }) {
  return (
    <div>
      <label className="data-label mb-1.5 block">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 font-mono ${props.className || ""}`}
    />
  );
}

export function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3 py-2 border border-stone-300 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 ${props.className || ""}`}
    >
      {children}
    </select>
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 ${props.className || ""}`}
    />
  );
}

// Length combobox: dropdown of standards + manual numeric entry. Pass list of presets.
export function LengthInput({ value, onChange, presets = [], listId = "length-presets", placeholder = "e.g. 3.0", ...rest }) {
  return (
    <>
      <input
        type="number"
        step="0.01"
        list={listId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-stone-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/30 focus:border-emerald-800 font-mono"
        {...rest}
      />
      <datalist id={listId}>
        {presets.map((p) => <option key={p} value={p} />)}
      </datalist>
    </>
  );
}

export function Modal({ open, onClose, title, children, footer, size = "md", testid }) {
  if (!open) return null;
  const widths = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-4xl", xl: "max-w-6xl" };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-950/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid={testid || "modal"}
    >
      <div
        className={`w-full ${widths[size]} bg-white rounded-md shadow-xl border border-stone-200 flex flex-col`}
        style={{ maxHeight: "calc(100vh - 2rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between shrink-0 bg-white rounded-t-md">
          <div className="font-display text-lg font-semibold">{title}</div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900 text-xl leading-none" data-testid="modal-close">×</button>
        </div>
        <div className="px-5 py-5 overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-end gap-2 rounded-b-md shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable delete/confirmation dialog.
export function ConfirmDialog({
  open, onClose, onConfirm, title = "Confirm", message,
  confirmLabel = "Delete", danger = true, testid = "confirm-dialog", busy = false,
}) {
  if (!open) return null;
  return (
    <Modal
      open={open} onClose={onClose} title={title} size="sm" testid={testid}
      footer={
        <>
          <Btn variant="secondary" onClick={onClose} testid="confirm-cancel">Cancel</Btn>
          <Btn variant={danger ? "danger" : "primary"} onClick={onConfirm} disabled={busy} testid="confirm-ok">
            {confirmLabel}
          </Btn>
        </>
      }
    >
      <div className="text-sm text-stone-700 leading-relaxed">{message}</div>
    </Modal>
  );
}
